import React, { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import Header from "./components/Header";
import MaintenanceTetris from "./components/maintenance";
import Footer from "./components/Footer";
import Home from "./pages/home";
import Login from "./pages/Login";
import NoMatch from "./pages/NoMatch";
import Profile from "./pages/profile";
import JobReport from "./pages/JobReport";
import JobPage from "./pages/JobPage";
import AdminPage from "./pages/admin";
import PrivateRoute from "./components/PrivateRoute";
import Calendar from "./components/Calendar";
import FeedbackModal from "./utils/FeedbackModal";
import { ModalForm } from "./components/Modal/ModalForm";
import OnboardingWizardModal from "./components/onboarding/OnboardingWizardModal";
// import UserJobStatsReport from "./pages/UserJobStatsReport";

import Auth from "./utils/auth";
import { useQuery, useSubscription } from "@apollo/client";
import { QUERY_ME } from "./utils/graphql/users/queries";
import { JOB_UPDATED_SUB } from "./utils/graphql/jobs/subscriptions";

const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === "true";

function JobWatcher({ enabled }) {
  const { data } = useSubscription(JOB_UPDATED_SUB, { skip: !enabled });

  useEffect(() => {
    if (!enabled) return;
    if (data) {
      console.log("Job updated:", data.jobUpdated);
    }
  }, [enabled, data]);

  return null;
}

export default function App() {
  console.log("Rendering App component");

  // Auth state
  const [loggedIn, setLoggedIn] = useState(Auth.loggedIn());
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showOnboardingPrompt, setShowOnboardingPrompt] = useState(false);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [dismissedOnboardingForSession, setDismissedOnboardingForSession] = useState(false);

  // Dark mode state — persisted to localStorage; defaults to system when unset
  const [colorMode, setColorMode] = useState(() => {
    const stored = localStorage.getItem("oplm-mode");
    if (stored === "light" || stored === "dark") return stored;
    return "system";
  });

  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (event) => {
      setSystemPrefersDark(event.matches);
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const effectiveMode = colorMode === "system" ? (systemPrefersDark ? "dark" : "light") : colorMode;
  const isDark = effectiveMode === "dark";

  const toggleColorMode = () => {
    const next = isDark ? "light" : "dark";
    setColorMode(next);
    localStorage.setItem("oplm-mode", next);
    document.documentElement.dataset.oplmMode = next;
    document.body.dataset.oplmMode = next;
  };

  const { data: meData, refetch: refetchMe } = useQuery(QUERY_ME, {
    skip: !loggedIn,
  });

  const me = meData?.me || null;
  const onboardingStatus = me?.onboarding || null;

  // Poll login status so UI stays in sync
  useEffect(() => {
    const interval = setInterval(() => {
      setLoggedIn(Auth.loggedIn());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!loggedIn) {
      setShowOnboardingPrompt(false);
      setShowOnboardingWizard(false);
      setDismissedOnboardingForSession(false);
      return;
    }

    if (!onboardingStatus?.required) {
      setShowOnboardingPrompt(false);
      setShowOnboardingWizard(false);
      return;
    }

    if (!dismissedOnboardingForSession && !showOnboardingWizard) {
      setShowOnboardingPrompt(true);
    }
  }, [loggedIn, onboardingStatus?.required, dismissedOnboardingForSession, showOnboardingWizard]);

  if (MAINTENANCE_MODE) {
    return (
      <div className={`flex-column justify-flex-start min-100-vh${isDark ? ' oplm-dark-override' : ''}`}>
        <Header maintenance={true} isDark={isDark} toggleColorMode={toggleColorMode} />
        <MaintenanceTetris />
        <Footer />
      </div>
    );
  }

  return (
    <div className={`flex-column justify-flex-start min-100-vh${isDark ? ' oplm-dark-override' : ''}`}>
      <Header maintenance={false} loggedIn={loggedIn} me={me} isDark={isDark} toggleColorMode={toggleColorMode} />
      <div className="container">
        <JobWatcher enabled={loggedIn} />

        <Routes>
          {/* Public */}
          <Route
            path="/login"
            element={loggedIn ? <Home /> : <Login />}
          />

          {/* Protected routes */}
          <Route element={<PrivateRoute loggedIn={loggedIn} />}>
            <Route path="/" element={<Home me={me}/>} />
            <Route path="/profile">
              <Route path=":username" element={<Profile me={me} />} />
              <Route path="" element={<Profile me={me} />} />
            </Route>
            <Route path="/my-calendar" element={<Calendar />} />
            <Route path="/job-report" element={<JobReport me={me} />} />
            <Route path="/jobs/:jobId" element={<JobPage me={me} />} />
            <Route path="/admin" element={<AdminPage />} />
            {/* <Route path="/user-job-stats-report" element={<UserJobStatsReport />} /> */}
            <Route path="*" element={<NoMatch />} />
          </Route>
        </Routes>
      </div>
      {loggedIn && me ? (
        <>
          <button
            type="button"
            onClick={() => setShowFeedbackModal(true)}
            className="btn btn-sm btn-primary position-fixed"
            style={{ right: 16, bottom: 16, zIndex: 1050 }}
            aria-label="Share feedback"
          >
            Feedback
          </button>
          {showFeedbackModal ? (
            <FeedbackModal authUser={me} onClose={() => setShowFeedbackModal(false)} />
          ) : null}

          {showOnboardingPrompt && onboardingStatus?.required ? (
            <ModalForm
              title="Complete your profile"
              onClose={() => {
                setDismissedOnboardingForSession(true);
                setShowOnboardingPrompt(false);
              }}
              footer={(
                <>
                  <button
                    type="button"
                    className="btn btn-outline-secondary me-auto"
                    onClick={() => {
                      setDismissedOnboardingForSession(true);
                      setShowOnboardingPrompt(false);
                    }}
                  >
                    Later
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setShowOnboardingPrompt(false);
                      setShowOnboardingWizard(true);
                    }}
                  >
                    Start now
                  </button>
                </>
              )}
            >
              <p className="mb-2">
                We found profile details that still need to be completed so matching can stay accurate.
              </p>
              <ul className="mb-0">
                <li>{onboardingStatus.missingProfileFields?.length || 0} missing core profile fields</li>
                <li>{onboardingStatus.missingAttributeKeys?.length || 0} missing profile attributes</li>
              </ul>
            </ModalForm>
          ) : null}

          {showOnboardingWizard && onboardingStatus?.required ? (
            <OnboardingWizardModal
              user={me}
              onboarding={onboardingStatus}
              onSkip={() => {
                setDismissedOnboardingForSession(true);
                setShowOnboardingWizard(false);
              }}
              onComplete={async () => {
                await refetchMe();
                setShowOnboardingWizard(false);
                setShowOnboardingPrompt(false);
                setDismissedOnboardingForSession(false);
              }}
            />
          ) : null}
        </>
      ) : null}
      <Footer />
    </div>
  );
}
